/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/enums.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import {
    CriticalVariableType,
    DataReviewTableColumnLevel,
    DataReviewTableColumnType,
    DataReviewType,
    DataStorageRegion,
    FieldDefinitionFieldType,
    NamedItemType,
    OrganisationMemberStatus,
    OrganisationType,
    PanelAggregate,
    PanelAverageLineThresholdType,
    PanelBenchmark,
    PanelColumnRangeCategory,
    PanelColumnType,
    PanelDisplayBy,
    PanelDisplayUnits,
    PanelFieldType,
    PanelGraphCategoryClause,
    PanelGraphType,
    PanelIntervalType,
    PanelLineCalculation,
    PanelModelType,
    PanelOrderBy,
    PanelPlotBy,
    PanelRateBy,
    PanelRateByType,
    PanelSeriesType,
    PanelSiteScoring,
    PanelTheme,
    PanelThresholdType,
    PanelTimePeriod,
    PhaseType,
    PanelType,
    RateGroupType,
    ReportDefinitionFieldTypes,
    ReportDefinitionTabs,
    ReportSignatoryRole,
    ReportStatus,
    RiskActionType,
    RiskEvaluationColor,
    RiskEvaluationType,
    RiskStatusType,
    RowDataSourceTable,
    StudyAutomationColor,
    StudyTimeUnit,
    SvtDependencyType,
    TimeFrequencyType,
    RiskEvaluationScoreTrendType,
    RiskDashboardPanel,
    ControlsAutomationColor,
    ProtocolDocumentStatus,
    RiqStatusType,
    StudyState,
} from 'types/graphql-global-types';

const Enums = {
    TimeFrequency: [
        TimeFrequencyType.NONE,
        TimeFrequencyType.DAILY,
        TimeFrequencyType.WEEKLY,
        TimeFrequencyType.MONTHLY,
        TimeFrequencyType.QUARTERLY,
        TimeFrequencyType.ANNUALLY,
    ],
    RiskStatus: [
        RiskStatusType.IDENTIFIED,
        RiskStatusType.ASSESSED,
        RiskStatusType.CONTROLLED,
        RiskStatusType.TRIGGERED,
        RiskStatusType.CLOSED,
        RiskStatusType.NOT_APPLICABLE,
        RiskStatusType.DRAFT,
        RiskStatusType.OBSOLETE,
    ],
    StudyCreationMethod: ['ManualThroughUI', 'StudyJsonImport', 'StudySpecificationImport'],
    CriticalVariableType: [
        CriticalVariableType.NONE,
        CriticalVariableType.PROCESS,
        CriticalVariableType.DATA,
        CriticalVariableType.BOTH,
    ],
    StudyState: [
        StudyState.LIVE,
        StudyState.READ_ONLY,
        StudyState.ARCHIVED,
        StudyState.DELETE_REQUESTED,
        StudyState.DELETE_CONFIRMED,
    ],
    ControlPhase: [PhaseType.DETECTION, PhaseType.PRE_STUDY, PhaseType.STUDY_CONDUCT, PhaseType.RESULTS_REPORTING],
    PanelType: [PanelType.MODEL, PanelType.GRAPH],
    PanelTheme: [PanelTheme.NONE, PanelTheme.THEME_1, PanelTheme.THEME_2, PanelTheme.THEME_3, PanelTheme.THEME_4],
    GraphType: [PanelGraphType.SCATTER_PLOT_LINE, PanelGraphType.HEATMAP, PanelGraphType.COLUMN, PanelGraphType.DONUT],
    GraphDisplayBy: [PanelDisplayBy.SUBJECT, PanelDisplayBy.SITE, PanelDisplayBy.COUNTRY, PanelDisplayBy.EVENT],
    GraphDisplayUnits: [PanelDisplayUnits.PERCENT, PanelDisplayUnits.COUNT, PanelDisplayUnits.PERCENT_BY_POPULATION],
    GraphTimePeriod: [
        PanelTimePeriod.DAYS,
        PanelTimePeriod.WEEKS,
        PanelTimePeriod.MONTHS,
        PanelTimePeriod.QUARTERS,
        PanelTimePeriod.YEARS,
    ],
    GraphSeries: [
        PanelSeriesType.SITE,
        PanelSeriesType.SUBJECT,
        PanelSeriesType.EVENT,
        PanelSeriesType.SITE_CUMULATIVE,
        PanelSeriesType.SITE_AVERAGE,
        PanelSeriesType.SUBJECT_BY_VISIT,
    ],
    GraphLineThresholdType: [
        PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN,
        PanelAverageLineThresholdType.STUDY_SPECIFIC,
    ],
    GraphLineCalculation: [PanelLineCalculation.MEAN, PanelLineCalculation.MEDIAN],
    ModelType: [
        PanelModelType.TRI_OPERATIONAL,
        PanelModelType.RATE_GROUP,
        PanelModelType.FUNNEL,
        PanelModelType.TRI_OPERATIONAL_TREND,
        PanelModelType.RATE_GROUP_TREND,
        PanelModelType.FUNNEL_TREND,
    ],
    ModelThreshold: [
        PanelThresholdType.SAMPLE_MEDIAN,
        PanelThresholdType.SAMPLE_MEAN,
        PanelThresholdType.STUDY_MEDIAN,
        PanelThresholdType.STUDY_MEAN,
        PanelThresholdType.STUDY_SPECIFIC,
    ],
    ModelDataInclusionPeriod: [
        PanelTimePeriod.DAYS,
        PanelTimePeriod.WEEKS,
        PanelTimePeriod.MONTHS,
        PanelTimePeriod.QUARTERS,
        PanelTimePeriod.YEARS,
    ],
    ModelCategoryRangePeriod: [
        PanelTimePeriod.DAYS,
        PanelTimePeriod.WEEKS,
        PanelTimePeriod.MONTHS,
        PanelTimePeriod.QUARTERS,
        PanelTimePeriod.YEARS,
    ],
    ModelStudyComparison: [PanelAggregate.MEAN, PanelAggregate.MEDIAN],
    ModelSiteScoring: [
        PanelSiteScoring.STANDARD,
        PanelSiteScoring.LOWER_GREEN,
        PanelSiteScoring.HIGHER_GREEN,
        PanelSiteScoring.LOWER_PURPLE,
        PanelSiteScoring.HIGHER_PURPLE,
    ],
    ModelConfidenceInterval: [PanelIntervalType.APPROXIMATE, PanelIntervalType.EXACT],
    ModelBenchmark: [PanelBenchmark.STUDY_MEAN, PanelBenchmark.STUDY_MEDIAN, PanelBenchmark.STUDY_SPECIFIC],
    ModelPlotBy: [PanelPlotBy.WEEKS, PanelPlotBy.MONTHS],
    ModelRateBy: [PanelRateBy.WEEKS, PanelRateBy.MONTHS, PanelRateBy.YEARS],
    DataSourceRateBy: [PanelRateByType.TIME_PERIOD, PanelRateByType.COUNT],
    DataSourceTimePeriod: [
        PanelTimePeriod.DAYS,
        PanelTimePeriod.WEEKS,
        PanelTimePeriod.MONTHS,
        PanelTimePeriod.QUARTERS,
        PanelTimePeriod.YEARS,
    ],
    Regions: [
        DataStorageRegion.OPRA_EU,
        DataStorageRegion.OPRA_US,
        /* DataStorageRegion.CUSTOM -- Removed for 5.0 (KM) */
    ],
    OrganisationMemberStatus: [
        OrganisationMemberStatus.INVITED,
        OrganisationMemberStatus.JOINED,
        OrganisationMemberStatus.VERIFYING,
    ],
    UserStatus: [OrganisationMemberStatus.INVITED, OrganisationMemberStatus.VERIFYING, OrganisationMemberStatus.JOINED],
    RiskAction: [RiskActionType.NONE, RiskActionType.CORRECTIVE, RiskActionType.PREVENTATIVE],
    EvaluationType: [RiskEvaluationType.ORIGINAL, RiskEvaluationType.RESCORE],
    EvaluationColour: [
        RiskEvaluationColor.RED,
        RiskEvaluationColor.AMBER,
        RiskEvaluationColor.GREEN,
        RiskEvaluationColor.NONE,
    ],
    EvaluationTrend: [
        RiskEvaluationScoreTrendType.NO_MOVEMENT,
        RiskEvaluationScoreTrendType.NO_TREND,
        RiskEvaluationScoreTrendType.FALLING,
        RiskEvaluationScoreTrendType.RISING,
        RiskEvaluationScoreTrendType.STABLE,
    ],
    StudyTimeUnit: [
        StudyTimeUnit.NONE,
        StudyTimeUnit.MINUTES,
        StudyTimeUnit.HOURS,
        StudyTimeUnit.DAYS,
        StudyTimeUnit.WEEKS,
        StudyTimeUnit.MONTHS,
        StudyTimeUnit.YEARS,
    ],
    ReportStatus: [ReportStatus.PREVIEW, ReportStatus.OBSOLETE, ReportStatus.FINAL],
    ReportSignatoryRole: [ReportSignatoryRole.AUTHOR, ReportSignatoryRole.APPROVER, ReportSignatoryRole.REVIEWER],
    NamedItemType: [
        NamedItemType.RISK_CATEGORY,
        NamedItemType.RISK_CONTROL_STRATEGY,
        NamedItemType.CONTROL_TYPE,
        NamedItemType.RESPONSIBLE_FUNCTION,
        NamedItemType.RISK_LIBRARY_CATEGORY,
        NamedItemType.DETECTION_TYPE,
        NamedItemType.RISK_OBSERVATION_CLASSIFICATION,
        NamedItemType.REVIEW_LOG_DECISION,
        NamedItemType.CTQ_TYPE,
        NamedItemType.CTQEDC_ENTRY,
        NamedItemType.VENDOR,
        NamedItemType.SM_REVIEW_TYPE,
        NamedItemType.CONTROLS_REVIEW_LOG_DECISION,
        NamedItemType.CONTROL_PHASE,
        NamedItemType.RISK_QUALITY_ISSUE,
        NamedItemType.SM_REVIEW_LOG_DECISION,
    ],
    SvtDependency: [
        SvtDependencyType.NONE,
        SvtDependencyType.COHORTS,
        SvtDependencyType.TREATMENT_ARMS,
        SvtDependencyType.BOTH,
    ],
    StudyAutomationColor: [
        StudyAutomationColor.NONE,
        StudyAutomationColor.RED_ONLY,
        StudyAutomationColor.RED_AMBER,
        StudyAutomationColor.RED_PURPLE,
        StudyAutomationColor.RED_AMBER_PURPLE,
    ],
    ControlsAutomationColor: [
        ControlsAutomationColor.NONE,
        ControlsAutomationColor.UPPER_RED,
        ControlsAutomationColor.LOWER_RED,
        ControlsAutomationColor.UPPER_RED_LOWER_RED,
        ControlsAutomationColor.UPPER_AMBER_UPPER_RED,
        ControlsAutomationColor.LOWER_AMBER_LOWER_RED,
        ControlsAutomationColor.UPPER_RED_UPPER_AMBER_LOWER_RED,
        ControlsAutomationColor.UPPER_RED_LOWER_AMBER_LOWER_RED,
        ControlsAutomationColor.UPPER_RED_UPPER_AMBER_LOWER_AMBER_LOWER_RED,
    ],
    OrgType: [OrganisationType.NONE, OrganisationType.CRO, OrganisationType.SPONSOR],
    FieldDefinitionFieldType: [
        FieldDefinitionFieldType.TEXT,
        FieldDefinitionFieldType.DATE,
        FieldDefinitionFieldType.PARTIAL_DATE,
        FieldDefinitionFieldType.INT,
        FieldDefinitionFieldType.FLOAT,
        FieldDefinitionFieldType.BOOL,
    ],
    DataSourceTableType: [
        RowDataSourceTable.NONE,
        RowDataSourceTable.STUDY,
        RowDataSourceTable.SITE,
        RowDataSourceTable.SITE_VISIT,
        RowDataSourceTable.SITE_ISSUE,
        RowDataSourceTable.SITE_PROTOCOL_DEVIATION,
        RowDataSourceTable.SUBJECT,
        RowDataSourceTable.SUBJECT_ADVERSE_EVENT,
        RowDataSourceTable.SUBJECT_ASSESSMENT,
        RowDataSourceTable.SUBJECT_CRF,
        RowDataSourceTable.SUBJECT_INFORMED_CONSENT,
        RowDataSourceTable.SUBJECT_ISSUE,
        RowDataSourceTable.SUBJECT_LAB,
        RowDataSourceTable.SUBJECT_PROTOCOL_DEVIATION,
        RowDataSourceTable.SUBJECT_QUERY,
        RowDataSourceTable.SUBJECT_VISIT,
        RowDataSourceTable.SUBJECT_CONCOMITANT_MEDICATION,
        RowDataSourceTable.SUBJECT_MEDICAL_HISTORY,
    ],
    DataBasedOnType: [
        DataReviewType.STUDY,
        DataReviewType.COUNTRY,
        DataReviewType.SITE,
        DataReviewType.SUBJECT,
        DataReviewType.DATA,
    ],
    ColumnLevelType: [
        DataReviewTableColumnLevel.NONE,
        DataReviewTableColumnLevel.STUDY,
        DataReviewTableColumnLevel.COUNTRY,
        DataReviewTableColumnLevel.SITE,
        DataReviewTableColumnLevel.SUBJECT,
    ],
    ColumnType: [
        DataReviewTableColumnType.STATIC,
        DataReviewTableColumnType.STANDARD_CALCULATION,
        DataReviewTableColumnType.PERCENTAGE,
        DataReviewTableColumnType.RATE,
        DataReviewTableColumnType.MAX,
        DataReviewTableColumnType.MIN,
        DataReviewTableColumnType.SUM,
        DataReviewTableColumnType.COUNT,
        DataReviewTableColumnType.COUNTRY_MEAN,
        DataReviewTableColumnType.STUDY_MEAN,
        DataReviewTableColumnType.SITE_MEAN,
        DataReviewTableColumnType.COUNTRY_MEDIAN,
        DataReviewTableColumnType.STUDY_MEDIAN,
        DataReviewTableColumnType.SITE_MEDIAN,
        DataReviewTableColumnType.PERCENTILE,
        DataReviewTableColumnType.PANEL,
    ],
    PanelFieldType: [
        PanelFieldType.STUDY_RATE,
        PanelFieldType.STUDY_SITE_MEAN,
        PanelFieldType.COUNTRY_SITE_MEAN,
        PanelFieldType.SITE_RATE,
    ],
    RateGroupType: [RateGroupType.NEGATIVE_BINOMIAL, RateGroupType.POISSON],
    AdminLevel: ['NONE', 'REVIEWER', 'CONTRIBUTOR', 'ADMINISTRATOR', 'SYSTEM_ADMINISTRATOR'],
    ShowHideThresholds: ['HIDE_UPPER_TR', 'HIDE_LOWER_TR', 'SHOW_ALL_TR'],
    OrderBy: [
        PanelOrderBy.LABEL_ASCENDING,
        PanelOrderBy.LABEL_DESCENDING,
        PanelOrderBy.VOLUME_ASCENDING,
        PanelOrderBy.VOLUME_DESCENDING,
        PanelOrderBy.VOLUME_BELL_CURVE,
        PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER,
    ],
    OrderByHeatmap: [
        `${PanelOrderBy.LABEL_ASCENDING}_HEATMAP`,
        `${PanelOrderBy.LABEL_DESCENDING}_HEATMAP`,
        null,
        null,
        null,
        `${PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER}_HEATMAP`,
    ],
    PanelColumnType: [`${PanelColumnType.STANDARD}_COLUMN_TYPE`, `${PanelColumnType.RANGED}_COLUMN_TYPE`],
    PanelGraphCategoryClauseEnum: [
        PanelGraphCategoryClause.NONE,
        PanelGraphCategoryClause.LESS_THAN_EQUAL,
        PanelGraphCategoryClause.LESS_THAN,
        PanelGraphCategoryClause.EQUAL,
        PanelGraphCategoryClause.MORE_THAN,
        PanelGraphCategoryClause.MORE_THAN_EQUAL,
    ],
    ColumnRangeCategory: [PanelColumnRangeCategory.NUMBER, PanelColumnRangeCategory.DATE],
    ReportDefinitionTabs: [
        ReportDefinitionTabs.RISKS,
        ReportDefinitionTabs.CRITICAL_VARIABLES,
        ReportDefinitionTabs.CONTROLS,
        ReportDefinitionTabs.RISKS_AND_CONTROLS,
        ReportDefinitionTabs.CRITICAL_VARIABLES_AND_CONTROLS,
        ReportDefinitionTabs.OBSERVATIONS,
        ReportDefinitionTabs.ACTIONS,
        ReportDefinitionTabs.OBSERVATIONS_AND_ACTIONS,
        ReportDefinitionTabs.RISKS_AND_CRITICAL_VARIABLES,
        ReportDefinitionTabs.FUNCTIONAL_DATA_REVIEWS,
        ReportDefinitionTabs.CRITICAL_VARIABLES_AND_FUNCTIONAL_DATA_REVIEWS,
        ReportDefinitionTabs.RISKS_AND_CM_CONTROL_HUB_CHARTS,
    ],
    ReportDefinitionFieldTypes: [
        ReportDefinitionFieldTypes.RISK_ID,
        ReportDefinitionFieldTypes.RISK_STATEMENT,
        ReportDefinitionFieldTypes.RISK_INTERNAL,
        ReportDefinitionFieldTypes.RISK_DATE_IDENTIFIED,
        ReportDefinitionFieldTypes.RISK_STATUS,
        ReportDefinitionFieldTypes.RISK_RESPONSIBLE_FUNCTION,
        ReportDefinitionFieldTypes.RISK_OWNER,
        ReportDefinitionFieldTypes.RISK_CONTROL_STRATEGY,
        ReportDefinitionFieldTypes.RISK_CATEGORY,
        ReportDefinitionFieldTypes.RISK_REVIEW_FREQUENCY_PERIOD,
        ReportDefinitionFieldTypes.RISK_REVIEW_FREQUENCY_UNIT,
        ReportDefinitionFieldTypes.RISK_REVIEW_STATUS,
        ReportDefinitionFieldTypes.RISK_SCORE,
        ReportDefinitionFieldTypes.RISK_RAG,
        ReportDefinitionFieldTypes.RISK_LIKELIHOOD,
        ReportDefinitionFieldTypes.RISK_IMPACT,
        ReportDefinitionFieldTypes.RISK_DETECTABILITY,
        ReportDefinitionFieldTypes.RISK_LAST_REVIEW_DATE,
        ReportDefinitionFieldTypes.RISK_NEXT_REVIEW_DATE,
        ReportDefinitionFieldTypes.CVID,
        ReportDefinitionFieldTypes.CV_CRITICAL,
        ReportDefinitionFieldTypes.CVISMP,
        ReportDefinitionFieldTypes.CV_ACTIVE,
        ReportDefinitionFieldTypes.CV_DESCRIPTION,
        ReportDefinitionFieldTypes.CV_JUSTIFICATION,
        ReportDefinitionFieldTypes.CV_TYPE,
        ReportDefinitionFieldTypes.CVEDC_ENTRY,
        ReportDefinitionFieldTypes.CV_RESPONSIBLE_VENDOR,
        ReportDefinitionFieldTypes.CV_SYSTEM,
        ReportDefinitionFieldTypes.CV_DATA_SOURCE,
        ReportDefinitionFieldTypes.CONT_ID,
        ReportDefinitionFieldTypes.CONT_TYPE,
        ReportDefinitionFieldTypes.CONT_OWNER,
        ReportDefinitionFieldTypes.CONT_DESCRIPTION,
        ReportDefinitionFieldTypes.CONT_MONITORING_PURPOSE,
        ReportDefinitionFieldTypes.CONT_MONITORING_VOLUME,
        ReportDefinitionFieldTypes.CONT_ADDITIONAL_INFORMATION,
        ReportDefinitionFieldTypes.CONT_DATE_IDENTIFIED,
        ReportDefinitionFieldTypes.CONT_RESPONSIBLE_FUNCTION,
        ReportDefinitionFieldTypes.CONT_REVIEW_FREQUENCY_PERIOD,
        ReportDefinitionFieldTypes.CONT_REVIEW_FREQUENCY_UNITS,
        ReportDefinitionFieldTypes.CONT_ACTIVE_DATE,
        ReportDefinitionFieldTypes.CONT_INACTIVATE_DATE,
        ReportDefinitionFieldTypes.CONT_THRESHOLD,
        ReportDefinitionFieldTypes.CONT_ACTION_TO_BE_TAKEN,
        ReportDefinitionFieldTypes.OBS_RISK_ID,
        ReportDefinitionFieldTypes.OBS_RISK_STATEMENT,
        ReportDefinitionFieldTypes.OBS_ID,
        ReportDefinitionFieldTypes.OBS_OBSERVATION,
        ReportDefinitionFieldTypes.OBS_POTENTIAL_CAUSE,
        ReportDefinitionFieldTypes.OBS_CONFIRMED_CAUSE,
        ReportDefinitionFieldTypes.OBS_RESPONSIBLE_FUNCTION,
        ReportDefinitionFieldTypes.OBS_OWNER,
        ReportDefinitionFieldTypes.OBS_CLASSIFICATION,
        ReportDefinitionFieldTypes.OBS_OBSERVATION_DATE,
        ReportDefinitionFieldTypes.OBS_CLOSED_DATE,
        ReportDefinitionFieldTypes.OBS_COMMENTS,
        ReportDefinitionFieldTypes.ACT_OBS_ID,
        ReportDefinitionFieldTypes.ACT_OBS,
        ReportDefinitionFieldTypes.ACT_ID,
        ReportDefinitionFieldTypes.ACT_TYPE,
        ReportDefinitionFieldTypes.ACT_OWNER,
        ReportDefinitionFieldTypes.ACT_RESPONSIBLE_FUNCTION,
        ReportDefinitionFieldTypes.ACT_REQUESTED_DATE,
        ReportDefinitionFieldTypes.ACT_DESCRIPTION,
        ReportDefinitionFieldTypes.ACT_COMMENTS,
        ReportDefinitionFieldTypes.ACT_RESULT,
        ReportDefinitionFieldTypes.ACT_EXPECTED_COMPLETION_DATE,
        ReportDefinitionFieldTypes.ACT_COMPLETED_DATE,
        ReportDefinitionFieldTypes.RISK_REVIEW_REQUIRED,
        ReportDefinitionFieldTypes.RISK_VENDORS,
        ReportDefinitionFieldTypes.RISK_COUNTRIES,
        ReportDefinitionFieldTypes.OBS_VENDORS,
        ReportDefinitionFieldTypes.OBS_COUNTRIES,
        ReportDefinitionFieldTypes.OBS_RESOLUTION,
        ReportDefinitionFieldTypes.CONT_PHASE,
        ReportDefinitionFieldTypes.FDR_ID,
        ReportDefinitionFieldTypes.FDR_DESCRIPTION,
        ReportDefinitionFieldTypes.FDR_RESPONSIBLE_FUNCTION,
        ReportDefinitionFieldTypes.FDR_COMMENT,
        ReportDefinitionFieldTypes.RISK_RATIONALE,
        ReportDefinitionFieldTypes.RISK_JUSTIFICATION,
        ReportDefinitionFieldTypes.RISK_QUALITY_ISSUE,
        ReportDefinitionFieldTypes.RISK_QUALITY_ISSUE_REFERENCE,
        ReportDefinitionFieldTypes.RISK_LINKED_CM_CONTROLS_COUNT,
        ReportDefinitionFieldTypes.RISK_OUT_OF_TOLERANCE,
        ReportDefinitionFieldTypes.RISK_OPEN_COMMENTS_COUNT,
        ReportDefinitionFieldTypes.RISK_CLOSED_DATE,
        ReportDefinitionFieldTypes.CV_RISK_COUNT,
        ReportDefinitionFieldTypes.CVFDR_COUNT,
        ReportDefinitionFieldTypes.CV_OPEN_COMMENTS_COUNT,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_PLOT_TITLE,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_ASSOCIATED_RISKS,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_DATASET,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_TIME_PERIOD,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_BENCHMARK,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_Y_AXIS_LABEL,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_OOT_VALUE,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_LOWER_AMBER,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_LOWER_RED,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_STATUS,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_UPPER_AMBER,
        ReportDefinitionFieldTypes.CM_CONTROLS_HUB_CHART_UPPER_RED,
    ],
    riskDashboardPanel: [
        RiskDashboardPanel.RISK_MAP,
        RiskDashboardPanel.STUDY_RISK_TRENDS,
        RiskDashboardPanel.RISK_CATEGORIES,
        RiskDashboardPanel.RISK_REVIEW_STATUS,
    ],
    ProtocolDocumentStatuses: [
        ProtocolDocumentStatus.PROCESSING,
        ProtocolDocumentStatus.IN_REVIEW,
        ProtocolDocumentStatus.REVIEW_COMPLETED,
        ProtocolDocumentStatus.DISCARDED,
    ],
    RiqStatus: [
        RiqStatusType.PENDING,
        RiqStatusType.REJECTED,
        RiqStatusType.ACCEPTED,
        RiqStatusType.ACCEPTED_AND_PROMOTED,
        RiqStatusType.IMPORTED,
    ],
};

export default Enums;
